"use client";

import React, { useState, useEffect, useContext, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import { useRouter } from "next/navigation";
import { AppRouterContext } from "next/dist/shared/lib/app-router-context.shared-runtime";
import { useLanguageVariables } from "@/contexts/LanguageVariablesContext";
import Link from "next/link";
import { useCaptchaConfig } from "@/hooks/useCaptchaConfig";
import { CaptchaService } from "@/services/CaptchaService";
import { AuthenticationService } from "@/services/AuthenticationService";
import { ErrorHandlerService } from "@/services/ErrorHandlerService";
import { resolveUrlFromMap } from "@/lib/blog-transformers";
import type { PageUrlMap } from "@/types/blog";
import AuthCaptchaField from "./AuthCaptchaField";

const setFieldValidity = (el: HTMLInputElement, message: string) => {
  el.setCustomValidity(message);
};

// SSR-safe router hook wrapper
// Backend'den render edildiğinde App Router context'i olmayabilir
function useSafeRouter() {
  const routerContext = useContext(AppRouterContext);
  try {
    const router = useRouter();
    return router;
  } catch (error) {
    // SSR ortamında router context yoksa null döndür
    if (typeof window === 'undefined' || !routerContext) {
      return null;
    }
    throw error;
  }
}

interface Agreement {
  id: number;
  title: string;
  slug: string;
  content?: string;
  is_required: boolean;
}

interface SocialProvider {
  provider: string;
  isActive: boolean;
  name?: string;
}

interface RegisterFormProps {
  language: string;
  languageId?: number;
  defaultLanguage: string;
  translations?: Record<string, string>;
  pageUrls?: PageUrlMap;
  agreements?: Agreement[];
  socialProviders?: SocialProvider[];
  /** Form modelinden (customer-registration); captcha zorunluluğu */
  requireCaptcha?: boolean;
}

export default function RegisterForm({
  language,
  languageId,
  defaultLanguage,
  translations,
  pageUrls,
  agreements: agreementsProp = [],
  socialProviders = [],
  requireCaptcha = true,
}: RegisterFormProps) {
  const { getVariable } = useLanguageVariables();
  const router = useSafeRouter();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    password: "",
    password_confirmation: "",
    newsletter_subscribed: true,
  });
  const [agreementsState, setAgreementsState] = useState<Record<number, boolean>>({});
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    feedback: "",
  });
  
  const [agreementsFromApi, setAgreementsFromApi] = useState<Agreement[] | null>(null);
  const agreements =
    agreementsProp.length > 0 ? agreementsProp : agreementsFromApi ?? [];

  const captchaConfig = useCaptchaConfig(requireCaptcha);
  const captchaServiceRef = useRef<CaptchaService | null>(null);
  const showCaptchaWidget =
    requireCaptcha &&
    !captchaConfig.isLoading &&
    Boolean(captchaConfig.config?.is_enabled && captchaConfig.config?.site_key);

  // Get translations with fallbacks
  const t = useCallback(
    (key: string, fallback: string) => {
      return translations?.[key] || getVariable(key, fallback);
    },
    [translations, getVariable]
  );

  // SSR'da agreements boş kaldıysa istemciden form detayını yükle
  useEffect(() => {
    if (agreementsProp.length > 0 || agreementsFromApi !== null) {
      return;
    }
    const params = new URLSearchParams({ form_slug: 'customer-registration' });
    if (languageId) {
      params.set('language_id', String(languageId));
    }
    let cancelled = false;
    fetch(`/api/forms/details?${params.toString()}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((payload: { success?: boolean; data?: { agreements?: { required?: Agreement[]; optional?: Agreement[] } } } | null) => {
        if (cancelled || !payload?.success || !payload.data?.agreements) {
          if (!cancelled) {
            setAgreementsFromApi([]);
          }
          return;
        }
        const required = payload.data.agreements.required ?? [];
        const optional = payload.data.agreements.optional ?? [];
        setAgreementsFromApi([...required, ...optional]);
      })
      .catch(() => {
        if (!cancelled) {
          setAgreementsFromApi([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [agreementsProp.length, agreementsFromApi, languageId]);

  // Initialize agreements state
  useEffect(() => {
    const state: Record<number, boolean> = {};
    agreements.forEach((agreement) => {
      state[agreement.id] = agreement.is_required; // Required agreements default to true
    });
    setAgreementsState(state);
  }, [agreements]);

  // Check password strength
  useEffect(() => {
    const password = formData.password;
    if (!password) {
      setPasswordStrength({ score: 0, feedback: "" });
      return;
    }

    let score = 0;
    const feedback: string[] = [];

    if (password.length >= 8) score++;
    else feedback.push(t("auth.register.validation.password_min", "At least 8 characters"));

    if (/[a-z]/.test(password)) score++;
    else feedback.push(t("auth.register.validation.password_lowercase", "Lowercase letter"));

    if (/[A-Z]/.test(password)) score++;
    else feedback.push(t("auth.register.validation.password_uppercase", "Uppercase letter"));

    if (/[0-9]/.test(password)) score++;
    else feedback.push(t("auth.register.validation.password_number", "Number"));

    if (/[@$!%*?&]/.test(password)) score++;
    else feedback.push(t("auth.register.validation.password_special", "Special character"));

    setPasswordStrength({
      score,
      feedback: feedback.length > 0 ? feedback.join(", ") : "",
    });
  }, [formData.password, t]);
  
  useEffect(() => {
    if (!requireCaptcha) {
      return;
    }
    if (captchaConfig.isLoading) {
      return;
    }

    if (!captchaConfig.config?.is_enabled || !captchaConfig.config?.site_key || !captchaConfig.config?.provider) {
      return;
    }
    
    let isMounted = true;
    
    const initCaptcha = async () => {
      if (!isMounted) {
        return;
      }
      
      try {
        if (!captchaConfig.config || !captchaConfig.config.provider || !captchaConfig.config.site_key) {
          return;
        }
        
        if (captchaServiceRef.current) {
          try {
            captchaServiceRef.current.remove();
          } catch (e) {
            // Ignore cleanup errors
          }
          captchaServiceRef.current = null;
        }
        
        const captchaService = new CaptchaService('register-captcha-widget-container');
        await captchaService.initialize({
          is_enabled: captchaConfig.config.is_enabled,
          provider: captchaConfig.config.provider,
          site_key: captchaConfig.config.site_key,
          script_url: captchaConfig.config.script_url || undefined,
        });
        
        if (isMounted) {
          captchaServiceRef.current = captchaService;
        } else {
          try {
            captchaService.remove();
          } catch (e) {
            // Ignore cleanup errors
          }
        }
      } catch (error) {
        // Silent fail
      }
    };
    
    initCaptcha();
    
    return () => {
      isMounted = false;
      if (captchaServiceRef.current) {
        try {
          captchaServiceRef.current.remove();
        } catch (e) {
          // Ignore cleanup errors
        }
        captchaServiceRef.current = null;
      }
    };
  }, [requireCaptcha, captchaConfig.config, captchaConfig.isLoading]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);

    const form = e.currentTarget;
    const passwordInput = form.querySelector<HTMLInputElement>('#password');
    const confirmInput = form.querySelector<HTMLInputElement>('#password_confirmation');
    if (passwordInput && confirmInput) {
      if (formData.password !== formData.password_confirmation) {
        setFieldValidity(
          confirmInput,
          t("auth.register.validation.password_confirmation", "Passwords do not match"),
        );
      } else {
        setFieldValidity(confirmInput, '');
      }
    }

    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    setLoading(true);

    // Sadece sözleşme varsa zorunlu kabul kontrolü yap
    if (agreements.length > 0) {
      const requiredAgreements = agreements.filter((a) => a.is_required);
      const allRequiredAccepted = requiredAgreements.every(
        (a) => agreementsState[a.id] === true
      );
      if (!allRequiredAccepted) {
        setError(t("auth.register.validation.agreements_required", "You must accept all required agreements"));
        setLoading(false);
        return;
      }
    }
    
    let captchaToken = '';
    const captchaNeeded =
      requireCaptcha &&
      !captchaConfig.isLoading &&
      Boolean(captchaConfig.config?.is_enabled && captchaConfig.config?.site_key);

    if (captchaNeeded) {
      if (!captchaServiceRef.current?.isEnabled()) {
        setError(t("auth.register.validation.captcha_required", "Please complete the verification."));
        setLoading(false);
        return;
      }
      try {
        captchaToken = await captchaServiceRef.current.getToken('register');
        if (!captchaToken) {
          setError(t("auth.register.validation.captcha_required", "Please complete the verification."));
          setLoading(false);
          return;
        }
      } catch (error) {
        setError(t("auth.register.validation.captcha_required", "Please complete the verification."));
        setLoading(false);
        return;
      }
    }

    try {
      const agreementsArray = Object.entries(agreementsState)
        .filter(([_, accepted]) => accepted)
        .map(([id]) => ({
          id: parseInt(id),
          accepted: true,
        }));

      const response = await AuthenticationService.register({
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        phone: formData.phone || undefined,
        password: formData.password,
        password_confirmation: formData.password_confirmation,
        newsletter_subscribed: formData.newsletter_subscribed,
        agreements: agreementsArray.length > 0 ? agreementsArray : [],
        captchaToken: captchaToken || undefined,
        language,
      });

      if (!response.success) {
        const errorMessage = ErrorHandlerService.extractErrorFromResponse(
          response,
          t("auth.register.error.message", "Registration failed")
        );
        throw new Error(errorMessage);
      }

      setSuccess(true);
      if (router) {
        setTimeout(async () => {
          if (!pageUrls) {
            router.push('/');
            return;
          }
          const { UrlBuilder } = await import('@/lib/url-builder');
          const { getDefaultLanguage } = await import('@/lib/language-detection');
          const defaultLang = await getDefaultLanguage();
          const builder = new UrlBuilder(pageUrls, language, defaultLang);
          const dashboardUrl = builder.buildCustomerPanelUrl('customer-dashboard');
          router.push(dashboardUrl);
        }, 2000);
      }
    } catch (err) {
      const errorMessage = ErrorHandlerService.cleanErrorMessage(
        err,
        t("auth.register.error.message", "Registration failed")
      );
      setError(errorMessage);
    } finally {
      if (captchaServiceRef.current?.isEnabled()) {
        try {
          captchaServiceRef.current.reset();
        } catch {
          // reset hatası görmezden gel
        }
      }
      setLoading(false);
    }
  };

  const handleSocialRegister = async (provider: string) => {
    try {
      // Get redirect URL from Next.js API
      const response = await fetch(`/api/customer/auth/social/${provider}/redirect`);
      const data = await response.json();

      if (data.success && data.data?.redirect_url) {
        window.location.href = data.data.redirect_url;
      } else {
        throw new Error(t("auth.social.provider_not_available", "Provider not available"));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t("auth.social.provider_not_available", "Provider not available"));
    }
  };

  const activeSocialProviders = socialProviders.filter((p) => p.isActive);
  const requiredAgreements = agreements.filter((a) => a.is_required);
  const optionalAgreements = agreements.filter((a) => !a.is_required);

  if (success) {
    return (
      <div className="w-full max-w-md mx-auto">
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
          <h3 className="font-semibold mb-2">
            {t("auth.register.success.title", "Registration Successful")}
          </h3>
          <p>{t("auth.register.success.message", "Please check your email to verify your account")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-md mx-auto">
      <form onSubmit={handleSubmit} noValidate className="space-y-6">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="flex flex-col gap-5">
            <label htmlFor="first_name" className="font-semibold text-base sm:text-lg text-alsero-dark-gray">
              {t("auth.register.form.first_name", "First Name")}
              <span className="text-red-500 ml-1">*</span>
            </label>
            <motion.input
              type="text"
              id="first_name"
              name="first_name"
              autoComplete="given-name"
              value={formData.first_name}
              onChange={(e) => {
                setFieldValidity(e.currentTarget, '');
                setFormData({ ...formData, first_name: e.target.value });
              }}
              onInvalid={(e) =>
                setFieldValidity(
                  e.currentTarget,
                  t("auth.register.validation.first_name_required", "First name is required"),
                )
              }
              onFocus={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              required
              className="border border-[rgba(0,0,0,0.08)] focus:!border-[rgba(0,0,0,0.40)] transition-all ease-in-out duration-300 rounded-lg text-[#4F545A] font-medium text-sm sm:text-[15px] px-4 sm:px-6 py-3 sm:py-4 w-full"
              placeholder={t("auth.register.form.first_name", "First Name")}
              whileHover={{ scale: 1.005, borderColor: "rgba(0, 0, 0, 0.15)" }}
              whileFocus={{ scale: 1.01, borderColor: "rgba(227, 30, 36, 0.3)" }}
              transition={{ type: "spring" as const, stiffness: 300, damping: 20 }}
            />
          </div>
          <div className="flex flex-col gap-5">
            <label htmlFor="last_name" className="font-semibold text-base sm:text-lg text-alsero-dark-gray">
              {t("auth.register.form.last_name", "Last Name")}
              <span className="text-red-500 ml-1">*</span>
            </label>
            <motion.input
              type="text"
              id="last_name"
              name="last_name"
              autoComplete="family-name"
              value={formData.last_name}
              onChange={(e) => {
                setFieldValidity(e.currentTarget, '');
                setFormData({ ...formData, last_name: e.target.value });
              }}
              onInvalid={(e) =>
                setFieldValidity(
                  e.currentTarget,
                  t("auth.register.validation.last_name_required", "Last name is required"),
                )
              }
              onFocus={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              required
              className="border border-[rgba(0,0,0,0.08)] focus:!border-[rgba(0,0,0,0.40)] transition-all ease-in-out duration-300 rounded-lg text-[#4F545A] font-medium text-sm sm:text-[15px] px-4 sm:px-6 py-3 sm:py-4 w-full"
              placeholder={t("auth.register.form.last_name", "Last Name")}
              whileHover={{ scale: 1.005, borderColor: "rgba(0, 0, 0, 0.15)" }}
              whileFocus={{ scale: 1.01, borderColor: "rgba(227, 30, 36, 0.3)" }}
              transition={{ type: "spring" as const, stiffness: 300, damping: 20 }}
            />
          </div>
        </div>

        <div className="flex flex-col gap-5">
          <label htmlFor="email" className="font-semibold text-base sm:text-lg text-alsero-dark-gray">
            {t("auth.register.form.email", "Email")}
            <span className="text-red-500 ml-1">*</span>
          </label>
          <motion.input
            type="email"
            id="email"
            name="email"
            autoComplete="email"
            value={formData.email}
            onChange={(e) => {
              setFieldValidity(e.currentTarget, '');
              setFormData({ ...formData, email: e.target.value });
            }}
            onInvalid={(e) => {
              const input = e.currentTarget;
              if (input.validity.valueMissing) {
                setFieldValidity(input, t("auth.register.validation.email_required", "Email is required"));
              } else if (input.validity.typeMismatch) {
                setFieldValidity(input, t("auth.register.validation.email_invalid", "Invalid email format"));
              }
            }}
            onFocus={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            required
            className="border border-[rgba(0,0,0,0.08)] focus:!border-[rgba(0,0,0,0.40)] transition-all ease-in-out duration-300 rounded-lg text-[#4F545A] font-medium text-sm sm:text-[15px] px-4 sm:px-6 py-3 sm:py-4 w-full"
            placeholder={t("auth.register.form.email", "Email")}
            whileHover={{ scale: 1.005, borderColor: "rgba(0, 0, 0, 0.15)" }}
            whileFocus={{ scale: 1.01, borderColor: "rgba(227, 30, 36, 0.3)" }}
            transition={{ type: "spring" as const, stiffness: 300, damping: 20 }}
          />
        </div>

        <div className="flex flex-col gap-5">
          <label htmlFor="phone" className="font-semibold text-base sm:text-lg text-alsero-dark-gray">
            {t("auth.register.form.phone", "Phone Number")} <span className="text-gray-500 text-sm">(Optional)</span>
          </label>
          <motion.input
            type="tel"
            id="phone"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            onFocus={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            className="border border-[rgba(0,0,0,0.08)] focus:!border-[rgba(0,0,0,0.40)] transition-all ease-in-out duration-300 rounded-lg text-[#4F545A] font-medium text-sm sm:text-[15px] px-4 sm:px-6 py-3 sm:py-4 w-full"
            placeholder={t("auth.register.form.phone", "Phone Number")}
            whileHover={{ scale: 1.005, borderColor: "rgba(0, 0, 0, 0.15)" }}
            whileFocus={{ scale: 1.01, borderColor: "rgba(227, 30, 36, 0.3)" }}
            transition={{ type: "spring" as const, stiffness: 300, damping: 20 }}
          />
        </div>

        <div className="flex flex-col gap-5">
          <label htmlFor="password" className="font-semibold text-base sm:text-lg text-alsero-dark-gray">
            {t("auth.register.form.password", "Password")}
            <span className="text-red-500 ml-1">*</span>
          </label>
          <motion.input
            type="password"
            id="password"
            name="password"
            autoComplete="new-password"
            value={formData.password}
            onChange={(e) => {
              setFieldValidity(e.currentTarget, '');
              setFormData({ ...formData, password: e.target.value });
            }}
            onInvalid={(e) => {
              const input = e.currentTarget;
              if (input.validity.valueMissing) {
                setFieldValidity(input, t("auth.register.validation.password_required", "Password is required"));
              } else if (input.validity.tooShort) {
                setFieldValidity(input, t("auth.register.validation.password_min", "Password must be at least 8 characters"));
              }
            }}
            onFocus={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            required
            minLength={8}
            className="border border-[rgba(0,0,0,0.08)] focus:!border-[rgba(0,0,0,0.40)] transition-all ease-in-out duration-300 rounded-lg text-[#4F545A] font-medium text-sm sm:text-[15px] px-4 sm:px-6 py-3 sm:py-4 w-full"
            placeholder={t("auth.register.form.password", "Password")}
            whileHover={{ scale: 1.005, borderColor: "rgba(0, 0, 0, 0.15)" }}
            whileFocus={{ scale: 1.01, borderColor: "rgba(227, 30, 36, 0.3)" }}
            transition={{ type: "spring" as const, stiffness: 300, damping: 20 }}
          />
          {formData.password && passwordStrength.feedback && (
            <p className="text-xs text-gray-600">{passwordStrength.feedback}</p>
          )}
        </div>

        <div className="flex flex-col gap-5">
          <label htmlFor="password_confirmation" className="font-semibold text-base sm:text-lg text-alsero-dark-gray">
            {t("auth.register.form.password_confirm", "Confirm Password")}
            <span className="text-red-500 ml-1">*</span>
          </label>
          <motion.input
            type="password"
            id="password_confirmation"
            name="password_confirmation"
            autoComplete="new-password"
            value={formData.password_confirmation}
            onChange={(e) => {
              setFieldValidity(e.currentTarget, '');
              setFormData({ ...formData, password_confirmation: e.target.value });
            }}
            onInvalid={(e) => {
              const input = e.currentTarget;
              if (input.validity.valueMissing) {
                setFieldValidity(input, t("auth.register.validation.password_required", "Password is required"));
              } else {
                setFieldValidity(
                  input,
                  t("auth.register.validation.password_confirmation", "Passwords do not match"),
                );
              }
            }}
            onFocus={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            required
            className="border border-[rgba(0,0,0,0.08)] focus:!border-[rgba(0,0,0,0.40)] transition-all ease-in-out duration-300 rounded-lg text-[#4F545A] font-medium text-sm sm:text-[15px] px-4 sm:px-6 py-3 sm:py-4 w-full"
            placeholder={t("auth.register.form.password_confirm", "Confirm Password")}
            whileHover={{ scale: 1.005, borderColor: "rgba(0, 0, 0, 0.15)" }}
            whileFocus={{ scale: 1.01, borderColor: "rgba(227, 30, 36, 0.3)" }}
            transition={{ type: "spring" as const, stiffness: 300, damping: 20 }}
          />
          {formData.password_confirmation && formData.password !== formData.password_confirmation && (
            <p className="text-xs text-red-600">
              {t("auth.register.validation.password_confirmation", "Passwords do not match")}
            </p>
          )}
        </div>

        {/* Agreements */}
        {agreements.length > 0 && (
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-gray-700">
              {t("auth.register.form.agreements", "Agreements")}
            </h3>

            {requiredAgreements.length > 0 && (
              <div className="space-y-2">
                {requiredAgreements.map((agreement) => (
                  <div key={agreement.id} className="flex items-start">
                    <input
                      id={`agreement-${agreement.id}`}
                      type="checkbox"
                      checked={agreementsState[agreement.id] || false}
                      onChange={(e) =>
                        setAgreementsState({ ...agreementsState, [agreement.id]: e.target.checked })
                      }
                      onFocus={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => e.stopPropagation()}
                      required
                      className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor={`agreement-${agreement.id}`} className="ml-2 block text-sm text-gray-700">
                      {agreement.title}
                    </label>
                  </div>
                ))}
              </div>
            )}

            {optionalAgreements.length > 0 && (
              <div className="space-y-2">
                {optionalAgreements.map((agreement) => (
                  <div key={agreement.id} className="flex items-start">
                    <input
                      id={`agreement-${agreement.id}`}
                      type="checkbox"
                      checked={agreementsState[agreement.id] || false}
                      onChange={(e) =>
                        setAgreementsState({ ...agreementsState, [agreement.id]: e.target.checked })
                      }
                      onFocus={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => e.stopPropagation()}
                      className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor={`agreement-${agreement.id}`} className="ml-2 block text-sm text-gray-700">
                      {agreement.title}
                    </label>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="flex items-center">
          <input
            id="newsletter"
            type="checkbox"
            checked={formData.newsletter_subscribed}
            onChange={(e) => setFormData({ ...formData, newsletter_subscribed: e.target.checked })}
            onFocus={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <label htmlFor="newsletter" className="ml-2 block text-sm text-gray-700">
            {t("form.newsletter_subscribe_label", "I would like to subscribe to the newsletter. You can check this to receive campaigns and news by email.")}
          </label>
        </div>

        {/* Honeypot field (hidden) */}
        <input type="text" name="honeypot" style={{ display: "none" }} tabIndex={-1} autoComplete="off" />
        
        <AuthCaptchaField
          containerId="register-captcha-widget-container"
          provider={captchaConfig.config?.provider}
          show={showCaptchaWidget}
          protectedLabel={t(
            "auth.register.captcha.protected",
            "This form is protected by reCAPTCHA. Your interaction is verified before registration.",
          )}
        />

        <button
          type="submit"
          disabled={loading || (requireCaptcha && captchaConfig.isLoading)}
          className="w-full bg-red-600 text-white py-3 px-4 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed uppercase font-semibold transition-colors"
        >
          {loading ? t("auth.register.form.submitting", "Registering...") : t("auth.register.form.submit", "SIGN UP")}
        </button>

        {activeSocialProviders.length > 0 && (
          <>
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">
                  {t("auth.register.form.or", "or")}
                </span>
              </div>
            </div>

            <div className="space-y-3">
              {activeSocialProviders.map((provider) => (
                <button
                  key={provider.provider}
                  type="button"
                  onClick={() => handleSocialRegister(provider.provider)}
                  className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md bg-white text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  {t(`auth.register.social.${provider.provider}`, provider.name || provider.provider)}
                </button>
              ))}
            </div>
          </>
        )}

        <p className="text-center text-sm text-gray-600">
          {t("auth.register.form.already_have_account", "Already have an account?")}{" "}
          {pageUrls?.['login'] ? (
            <Link
              href={resolveUrlFromMap(pageUrls, 'login', language, defaultLanguage, '/login')}
              className="text-red-600 hover:text-red-800"
            >
              {t("auth.register.form.login_link", "Login")}
            </Link>
          ) : (
            <span className="text-red-600">{t("auth.register.form.login_link", "Login")}</span>
          )}
        </p>
      </form>
    </div>
  );
}

